"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";

/* DM pattern: 56px gap between sticky tops, starting at 100px */
const stickyTops = [80, 120, 160, 200, 240, 280];
const zIndexes = [1, 2, 3, 4, 5, 6];

const projects = [
  {
    category: "Presentation Design",
    color: "#C6B8FF",
    heading: "Premium Lifestyle Pitch Decks",
    description: "A collection of pitch decks for lifestyle and FMCG brands. Clean layouts with product photography, market positioning slides, and consumer insight visuals — ready for investor meetings.",
    stat1: { label: "Slides Designed", value: "50+" },
    stat2: { label: "Turnaround", value: "5-7 Days" },
    author: "Nabeel Imdaad",
    role: "Lead Designer, Dev Cubix",
    image: "/assets/portfolio/presentation-design/13-premium-lifestyle.jpg",
    href: "/projects",
  },
  {
    category: "UI/UX Design",
    color: "#E8DCC8",
    heading: "Mirage Glamping Resort",
    description: "Corporate website design for a luxury glamping resort. Immersive hero imagery, room galleries, booking flows, and experience showcases with earthy tones and elegant typography.",
    stat1: { label: "Screens Designed", value: "20+" },
    stat2: { label: "Duration", value: "8 Weeks" },
    author: "Nabeel Imdaad",
    role: "Lead Designer, Dev Cubix",
    image: "/assets/portfolio/uiux-design/01-mirage-glamping-cover.webp",
    href: "/projects",
  },
  {
    category: "Web Development",
    color: "#C8E0D8",
    heading: "SafeFound Fintech Platform",
    description: "A fintech landing page with data visualizations, security feature breakdowns, and onboarding flows. Dark theme with trust-building UI patterns built in Next.js and TypeScript.",
    stat1: { label: "Pages Built", value: "8" },
    stat2: { label: "Duration", value: "10 Weeks" },
    author: "Bilal Ahmad",
    role: "Lead Developer, Dev Cubix",
    image: "/assets/portfolio/web-development/02-safefound-fintech/01.png",
    href: "/projects",
  },
  {
    category: "Graphic Design",
    color: "#C0D4F0",
    heading: "Imagine Geometric Brand Identity",
    description: "Brand guidelines for a space technology company. Includes logo usage, typography system, color palette, and layout standards — all packaged in a professional brand book.",
    stat1: { label: "Brand Assets", value: "15+" },
    stat2: { label: "Duration", value: "2 Weeks" },
    author: "Nabeel Imdaad",
    role: "Lead Designer, Dev Cubix",
    image: "/assets/portfolio/graphic-design/brand-06-hitchhiker.png",
    href: "/projects",
  },
  {
    category: "Webflow & Framer",
    color: "#D8E4C0",
    heading: "TYT.TRUP Travel Agency",
    description: "A travel agency website built on Webflow with destination imagery, trip planners, and booking flows. Smooth scroll animations and CMS integration for easy content updates.",
    stat1: { label: "Pages", value: "12" },
    stat2: { label: "Duration", value: "5 Weeks" },
    author: "Bilal Ahmad",
    role: "Lead Developer, Dev Cubix",
    image: "/assets/portfolio/webflow-framer/01-tyttrup-travel.jpg",
    href: "/projects",
  },
  {
    category: "AI & Chatbots",
    color: "#E8DDF5",
    heading: "Cortex AI Chatbot Platform",
    description: "UI design for a SaaS chatbot platform with conversational flows, smart suggestions, and knowledge base management. Light theme with clean interaction patterns.",
    stat1: { label: "Screens Designed", value: "25+" },
    stat2: { label: "Duration", value: "6 Weeks" },
    author: "Bilal Ahmad",
    role: "Lead Developer, Dev Cubix",
    image: "/assets/portfolio/ai-chatbots/01-sona-ai-assistant.jpg",
    href: "/projects",
  },
];

export default function IndustryWins() {
  return (
    <section className="bg-surface">
      <div className="max-w-[1280px] mx-auto px-6 pt-10 sm:pt-16 md:pt-20 pb-4 sm:pb-8">
        {/* Header */}
        <motion.div
          className="text-center mb-8 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="section-badge mb-4">Industry Wins</p>
          <h2 className="text-2xl sm:text-3xl md:text-[48px] font-bold text-foreground leading-[1.2] font-ui">
            <span>Proven Success in </span>
            <span className="font-serif italic text-purple-light">Every Industry</span>
          </h2>
        </motion.div>
      </div>

      {/* Sticky stacking cards */}
      <div className="max-w-[1280px] mx-auto px-6 pb-8">
        {projects.map((project, i) => (
          <motion.div
            key={i}
            className="md:sticky rounded-[16px] mb-4 md:mb-6"
            style={{
              top: `${stickyTops[i]}px`,
              zIndex: zIndexes[i],
            }}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.25, 1, 0.5, 1] }}
            viewport={{ once: true, margin: "-60px" }}
          >
            <div
              className="rounded-[16px] overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500"
              style={{ backgroundColor: project.color }}
            >
              {/* DM layout: flex row, text left + image right, 20px padding, 40px left */}
              <div className="flex flex-col md:flex-row md:items-stretch p-4 sm:p-5 md:pl-10 md:pr-5 md:py-5">
                {/* Text content — fixed width like DM (~470px) */}
                <div className="flex flex-col justify-between md:w-[400px] lg:w-[470px] md:flex-shrink-0 py-2">
                  <div>
                    <span className="inline-block mb-4 text-sm font-serif italic text-[#0A0A0A]/80">
                      {project.category}
                    </span>
                    <h3 className="text-2xl md:text-[32px] font-bold text-[#0A0A0A] mb-4 font-sans leading-tight">
                      {project.heading}
                    </h3>
                    <p className="text-[rgb(82,82,82)] text-sm md:text-[15px] leading-relaxed mb-6 font-body max-w-full sm:max-w-[420px]">
                      {project.description}
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="flex gap-4 sm:gap-10 mb-6">
                    <div>
                      <p className="text-xs text-[#0A0A0A]/50 mb-1">{project.stat1.label}</p>
                      <p className="text-xl font-bold text-[#0A0A0A]">{project.stat1.value}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#0A0A0A]/50 mb-1">{project.stat2.label}</p>
                      <p className="text-xl font-bold text-[#0A0A0A]">{project.stat2.value}</p>
                    </div>
                  </div>

                  {/* Author + CTA */}
                  <div className="flex items-center justify-between max-w-full sm:max-w-[420px]">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#0A0A0A]/15 flex items-center justify-center text-[#0A0A0A] font-bold text-sm">
                        {project.author[0]}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#0A0A0A]">{project.author}</p>
                        <p className="text-xs text-[#0A0A0A]/60">{project.role}</p>
                      </div>
                    </div>
                    <Link
                      href={project.href}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A0A0A] hover:opacity-70 transition-opacity py-2 -my-2"
                    >
                      View Project
                      <span className="text-lg">→</span>
                    </Link>
                  </div>
                </div>

                {/* Image — grows to fill remaining space, DM style */}
                <div className="flex-1 md:ml-10 mt-5 md:mt-0 flex items-center justify-end">
                  <motion.div
                    className="relative w-full rounded-lg overflow-hidden"
                    style={{ aspectRatio: "3 / 2" }}
                    initial={{ opacity: 0, scale: 0.75 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, ease: [0.25, 1, 0.5, 1] }}
                    viewport={{ once: true }}
                  >
                    <Image
                      src={project.image}
                      alt={project.heading}
                      fill
                      className="object-cover hover:scale-110 hover:-rotate-2 transition-transform duration-[350ms] ease-[cubic-bezier(0.45,0,0.55,1)]"
                    />
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* See all CTA */}
      <div className="text-center py-8">
        <Link
          href="/projects"
          className="inline-flex items-center gap-2 px-8 py-4 bg-red-accent text-white rounded-full font-semibold text-sm hover:bg-red-accent/90 transition-colors"
        >
          See All Projects
          <Image src="/assets/arrows/CTA-Arrow-default.svg" alt="" width={14} height={14} className="brightness-0 invert" />
        </Link>
      </div>
    </section>
  );
}
